import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Select from '@mui/material/Select';
import { styled, keyframes } from '@mui/material/styles';

// Мерцание звезд
const twinkle = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`;

// Функция создаёт строку с radial-gradient для звезд
function generateStars(count: number, size: number) {
    const stars: string[] = [];
    for (let i = 0; i < count; i++) {
        const x = Math.random() * 100; // %
        const y = Math.random() * 100; // %
        stars.push(`radial-gradient(${size}px ${size}px at ${x}% ${y}%, white, transparent)`);
    }
    return stars.join(',');
}

// Светлый градиентный фон
export const StarryBackground = styled('div')(() => {
    return {
        position: 'relative',
        height: '100vh',
        width: '100vw',
        background: 'linear-gradient(110deg, rgba(59, 190, 219, 1) 20%, rgba(238, 234, 252, 1) 112%, rgba(227, 247, 250, 1) 100%)',
        overflow: 'hidden',
    };
});

// Эффект туманности
export const NebulaEffect = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `
        radial-gradient(ellipse at 20% 50%, rgba(59, 190, 219, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 20%, rgba(34, 211, 238, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse at 40% 80%, rgba(6, 182, 212, 0.25) 0%, transparent 50%)
    `,
    animation: 'nebulaDrift 30s ease-in-out infinite',
    zIndex: 2,
    '@keyframes nebulaDrift': {
        '0%, 100%': {
            transform: 'translateX(0) translateY(0) rotate(0deg)',
            opacity: 0.7,
        },
        '25%': {
            transform: 'translateX(-20px) translateY(-10px) rotate(1deg)',
            opacity: 0.8,
        },
        '50%': {
            transform: 'translateX(20px) translateY(20px) rotate(-1deg)',
            opacity: 0.6,
        },
        '75%': {
            transform: 'translateX(10px) translateY(-20px) rotate(0.5deg)',
            opacity: 0.9,
        },
    },
}));

// Контейнер для контента
export const ContentContainer = styled(Box)(() => ({
    position: 'relative',
    zIndex: 10,
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

// Заголовок с логотипом
export const HeaderContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '16px 24px',
    borderBottom: '1px solid gray',
}));

export const TitleContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
}));

// Кнопка назад в космическом стиле
export const BackButton = styled(Button)(() => ({
    color: 'white',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
        color: 'rgba(6, 182, 212, 0.8)',
        textShadow: '0 0 10px rgba(6, 182, 212, 0.5)',
        backgroundColor: 'transparent',
    },
}));

// Стилизованная таблица
export const StyledTableContainer = styled(TableContainer)(() => ({
    flex: 1,
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)',
    backdropFilter: 'blur(16px)',
    border: '1px solid gray',
    borderRadius: '12px',
    
    // Кастомные стили для скроллбара
    '&::-webkit-scrollbar': {
        width: '20px !important',
        height: '20px !important',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(30, 58, 138, 0.2)',
        borderRadius: '10px',
        border: '1px solid gray',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.8) 0%, rgba(59, 130, 246, 0.7) 100%)',
        borderRadius: '10px',
        border: '1px solid gray',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(30, 58, 138, 1) 0%, rgba(59, 130, 246, 0.9) 100%)',
            borderColor: 'gray',
            boxShadow: '0 0 12px rgba(30, 58, 138, 0.5)',
        },
        '&:active': {
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 1) 0%, rgba(30, 58, 138, 1) 100%)',
            boxShadow: '0 0 18px rgba(30, 58, 138, 0.7)',
        },
    },
    '&::-webkit-scrollbar-corner': {
        background: 'rgba(30, 58, 138, 0.2)',
    },
    
    // Firefox стили для скроллбара
    scrollbarWidth: 'auto',
    scrollbarColor: 'rgba(30, 58, 138, 0.8) rgba(30, 58, 138, 0.2)',
    
    '& .MuiTable-root': {
        backgroundColor: 'transparent',
    },
    '& .MuiTableHead-root': {
        '& .MuiTableCell-root': {
            color: 'white',
            borderBottom: '1px solid gray',
            borderRight: '1px solid gray',
            fontWeight: 600,
        },
    },
    '& .MuiTableBody-root': {
        '& .MuiTableCell-root': {
            color: 'white',
            borderBottom: '1px solid gray',
            borderRight: '1px solid gray',
        },
        '& .MuiTableRow-root:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
        },
    },
}));

// Стилизованная кнопка загрузки
export const StyledDownloadButton = styled(Button)(() => ({
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)',
    backdropFilter: 'blur(16px)',
    border: '1px solid gray',
    color: 'white',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '8px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(6, 182, 212, 0.4) 100%)',
        borderColor: 'gray',
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
    },
    '&:disabled': {
        background: 'rgba(59, 130, 246, 0.1)',
        color: 'rgba(255, 255, 255, 0.5)',
        borderColor: 'gray',
    },
}));

// Стилизованный Select
export const StyledSelect = styled(Select)(() => ({
    color: 'white',
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'gray',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'gray',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'gray',
    },
    '& .MuiSelect-icon': {
        color: 'white',
    },
}));

// Стилизованная пагинация
export const StyledPagination = styled(Box)(() => ({
    '& .MuiPaginationItem-root': {
        color: 'white',
        border: '1px solid gray',
        '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            borderColor: 'gray',
        },
        '&.Mui-selected': {
            backgroundColor: 'rgba(59, 130, 246, 0.4)',
            borderColor: 'gray',
        },
    },
}));

// Футер с элементами управления
export const FooterContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: '8px 16px',
    borderTop: '1px solid gray',
    backgroundColor: 'rgba(30, 41, 59, 0.3)',
    backdropFilter: 'blur(16px)',
    marginBottom: '20px'
}));

// Стилизованный текст
export const StyledTypography = styled(Typography)(() => ({
    color: 'rgba(147, 197, 253, 0.8)',
    fontSize: '14px',
}));
